import React, { useEffect, useMemo, useState, useCallback } from 'react';
import PostListDesktop from './Desktop/PostList';
import PostListMobile from './Mobile/PostList';
import { readBattleGroups, writeBattleGroups } from '../utils/groupCache';
import { useBackHandler } from '../hooks/useBackHandler';

/**
 * SWEX 로 수집한 점령전 확정 기록(SiegeBattle 컬렉션)을 기존 전투기록 화면으로 보여준다.
 *
 * 디자인·기능을 새로 만들지 않고 PostList 를 그대로 재사용한다.
 * 그래서 이 컴포넌트가 하는 일은 사실상 두 가지뿐이다.
 *   1) SiegeBattle 문서를 PostList 가 읽는 필드 모양으로 변환
 *   2) 매치(그룹) 선택 -> 목록 흐름과 필터 상태 관리
 *
 * 이전 길드(가짜섬린이) 시절 Post 컬렉션 기록은 삭제됐고, 이제 이 컬렉션만 본다.
 */

interface Props {
    isMobile: boolean;
    monsterMapping: any;
    user: any;
}

interface Unit {
    unitMasterId: number;
    name: string;
    attribute: string;
    displayName: string;
}

interface Battle {
    logId: number;
    /** 1 공격 · 2 방어 — 전투 열쇠에 들어간다 */
    logType?: number;
    siegeId: number;
    matchId: number;
    baseNumber: number;
    battledAt: string;
    wizardName: string;
    /** 서버가 붙여 주는 "지금 명단에 없는 사람" 표시 */
    former?: boolean;
    guildName: string;
    oppWizardName: string;
    oppGuildName: string;
    result: 'win' | 'lose';
    attackTeam: Unit[];
    defenseTeam: Unit[];
}

/**
 * 서버가 `?decks=ids` 로 내려주는 모양. 덱이 아이디 배열이다.
 *
 * 몹 이름·속성·한글명은 전투마다 되풀이되는 값이라 목록에서 가장 무거웠다
 * (한 건에 6마리). 이름표는 `/api/monsters/unit-index` 로 **한 번만** 받아
 * 브라우저에 저장해 두고, 받은 기록에 여기서 붙인다.
 */
interface SlimBattle extends Omit<Battle, 'attackTeam' | 'defenseTeam'> {
    atk: number[];
    def: number[];
}

/** `unit_master_id → [영문명, 속성, 한글명]` */
type UnitIndex = Record<string, [string, string, string]>;

const UNIT_INDEX_KEY = 'siege-unit-index-v1';

/**
 * 몹 이름표. 몹 동기화 때만 바뀌므로 브라우저에 남겨 두고 쓴다.
 *
 * `localStorage` 를 쓴다(세션이 아니라) — 새로고침·재로그인해도 다시 받지 않는다.
 * 서버가 주는 `version` 이 달라지면(새 몹이 등록되면) 버리고 새로 받는다.
 */
async function loadUnitIndex(): Promise<UnitIndex> {
    let saved: { version: string; units: UnitIndex } | null = null;
    try {
        const raw = localStorage.getItem(UNIT_INDEX_KEY);
        if (raw) saved = JSON.parse(raw);
    } catch {
        /* 못 읽으면 받아온다 */
    }

    try {
        const d = await fetch('/api/monsters/unit-index').then((r) => r.json());
        if (d?.success && d.units) {
            if (!saved || saved.version !== d.version) {
                try {
                    localStorage.setItem(UNIT_INDEX_KEY, JSON.stringify({ version: d.version, units: d.units }));
                } catch {
                    /* 저장 실패는 넘긴다 — 이번 판만 메모리로 쓴다 */
                }
            }
            return d.units as UnitIndex;
        }
    } catch {
        /* 못 받으면 저장해 둔 것으로 버틴다 */
    }
    return saved?.units ?? {};
}

/** 아이디 배열을 화면이 읽는 몹 모양으로 되돌린다 */
function hydrateDeck(ids: number[], index: UnitIndex): Unit[] {
    return (ids || []).map((id) => {
        const e = index[String(id)];
        return {
            unitMasterId: id,
            name: e ? e[0] : `UNKNOWN_${id}`,
            attribute: e ? e[1] : '',
            displayName: e ? e[2] : `미등록(${id})`
        };
    });
}

function hydrateBattle(b: SlimBattle, index: UnitIndex): Battle {
    const { atk, def, ...rest } = b;
    return { ...rest, attackTeam: hydrateDeck(atk, index), defenseTeam: hydrateDeck(def, index) };
}

const POSTS_PER_PAGE = 20;

/**
 * 받아 둔 것을 모듈에 남겨 둔다.
 *
 * 탭을 옮기면 이 화면이 통째로 사라졌다 다시 만들어진다(PageContainer 가 그렇게 그린다).
 * 그때마다 다시 받으면 들어올 때마다 "불러오는 중"이 뜬다. 남겨 둔 값이 있으면 먼저
 * 그리고, 뒤에서 조용히 새로 받아 달라진 것만 바꾼다.
 *
 * 세션 저장소에 넣지 않는 이유는 기록이 시즌당 몇 MB라 저장 한도를 넘기기 때문이다.
 * 새로고침하면 사라지는데, 오래된 값을 오래 붙들지 않으려면 그 편이 맞다.
 */
const cache: {
    avatars: { avatars: Record<string, string>; kicked: string[] } | null;
    groups: Map<number, SiegeGroup[]>;
    battles: Map<string, Battle[]>;
} = { avatars: null, groups: new Map(), battles: new Map() };

/**
 * 시즌 구조는 서버에 있다.
 *
 *   전투기록(SiegeBattle) → 그룹(SiegeGroup) → 시즌
 *
 * 그룹 = 1:1:1 매치업 한 판이고, 시즌은 그룹 문서가 들고 있다. 게임은 시즌을
 * 알려주지 않으므로(siegeId 2026080201 은 날짜 기반 고유값일 뿐이다) 사람이
 * 원격 앱에서 정한다. 여기서는 그룹에 적힌 시즌을 그대로 읽는다.
 * (예전에는 이 파일에 상수 22 가 박혀 있었고, 그다음엔 siegeId 대응표를 받아 썼다.)
 */
interface SeasonInfo {
    current: number;
    seasons: number[];
}

/** 서버가 내려주는 매치업 그룹 (GET /api/siege-battles/groups) */
interface SiegeGroup {
    _id: string;
    groupId: string;
    siegeId: number;
    season: number;
    groupName: string;
    opponentGuilds: string[];
    startDate: string;
    count: number;
    /**
     * 서버가 세어 준 집계. 그룹 카드가 이걸 쓴다 — 기록을 받지 않아도 된다.
     *   wins/losses    지금 보고 있는 종류(공격/방어) 기준
     *   winRate        우리가 공격해서 이긴 비율
     *   defenseRate    우리 방덱이 막아낸 비율
     */
    wins?: number;
    losses?: number;
    winRate?: number;
    defenseRate?: number;
}

/** 빈 슬롯(unit_master_id 0)은 기존 파이프라인과 마찬가지로 덱에서 뺀다. */
const realUnits = (units: Unit[]) => (units || []).filter((u) => u.unitMasterId !== 0 && u.name !== 'empty');

/**
 * PostList 가 읽는 필드로 변환한다. 없는 개념(좋아요/댓글/조회수)은 빈 값으로 채운다.
 * groupName 은 매치업 단위로 미리 계산해 넘긴다.
 */
function toPost(
    b: Battle,
    groupName: string,
    logType: number,
    season: number,
    avatars: Record<string, string>,
    /** 추방된 사람 이름들. 이름을 연한 빨강으로 그리는 데 쓴다 */
    kicked: Set<string>
) {
    const atk = realUnits(b.attackTeam);
    const def = realUnits(b.defenseTeam);
    // 방어 기록은 게임이 덱을 하나만 준다. 왼쪽(공덱 자리)에 놓아 한 줄로 보이게 한다.
    const single = logType === 2;
    const soloDeck = atk.length ? atk : def;

    return {
        // 전투 열쇠(서버 utils/battleRef) — 전투 번호만으로는 다른 점령전 전투와 겹친다
        _id: `siege-${b.siegeId}-${b.logType ?? logType}-${b.logId}`,
        logId: b.logId,
        title: `${b.wizardName} vs ${b.oppWizardName}`,
        comment: '',
        author: b.wizardName,
        nickname: b.wizardName,
        // 전투기록에는 닉네임만 있다. 얼굴은 서버가 준 닉네임→이미지 대응표에서 찾는다.
        // 없으면 빈 값으로 두어 화면이 기본 이미지를 쓰게 한다.
        profileImg: avatars[b.wizardName] || '',
        /**
         * 이름을 연한 빨강으로 그릴지.
         *   former  — 서버가 판정한 "지금 명단에 없는 사람"(계정이 없어도 잡힌다)
         *   kicked  — 계정이 추방된 사람
         */
        isDeleted: (b as any).former === true || kicked.has(b.wizardName),
        defenderNickname: b.oppWizardName,
        defenderProfileImg: avatars[b.oppWizardName] || '',
        attackTeam: single ? soloDeck : atk,
        defenseTeam: single ? [] : def,
        result: b.result,
        season,
        // 그룹 = 1:1:1 매치업 한 판. 상대 길드로 쪼개면 같은 매치업이 갈라진다.
        group: groupName,
        // 표시는 상대 닉네임, 아이콘은 상대 길드 엠블럼을 쓴다
        siegeWar: b.oppWizardName,
        siegeWarImg: b.oppGuildName,
        siegeId: b.siegeId,
        createdAt: b.battledAt,
        views: 0,
        likes: [],
        dislikes: [],
        comments: [],
        // 새 데이터임을 표시해 두면 나중에 화면에서 구분하기 쉽다
        source: 'swex' as const,
        baseNumber: b.baseNumber,
        // 공덱 점수 — 그 판 룬으로 시뮬레이션 300번 돌린 공덱 승률·등급(서버 utils/simGrade). 아직 안 매긴 판은 없다
        simWin: (b as any).simWin as number | undefined,
        simGrade: (b as any).simGrade as string | undefined
    };
}

const SiegeBattleList: React.FC<Props> = ({ isMobile, monsterMapping, user }) => {
    // 1 = 공격 기록(우리가 친 전투), 2 = 방어 기록(상대가 우리를 친 전투)
    const [logType, setLogType] = useState<1 | 2>(1);
    /** 기록을 마지막으로 받아온 시각 (공격·방어가 따로 쌓이므로 고른 쪽 기준이다) */
    const [syncedAt, setSyncedAt] = useState<string | null>(null);
    // 그룹 구성은 서버가 정한다. 화면마다 따로 계산하면 길드원 탭과 어긋난다.
    const [groups, setGroups] = useState<SiegeGroup[]>([]);
    const [selectedSeason, setSelectedSeason] = useState(0);
    // 기존 전투기록 화면과 같은 흐름: 시즌 선택 -> 매치업 선택 -> 목록
    const [battleStep, setBattleStep] = useState<'season' | 'group' | 'list'>('season');

    const [battles, setBattles] = useState<Battle[]>([]);
    /** 길드원 탭용 공격 기록 — [방어] 를 골랐을 때만 따로 받는다(공격이면 `battles` 가 곧 그것) */
    const [attackBattles, setAttackBattles] = useState<Battle[] | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [seasonInfo, setSeasonInfo] = useState<SeasonInfo | null>(null);
    const [avatars, setAvatars] = useState<Record<string, string>>({});
    const [kicked, setKicked] = useState<Set<string>>(new Set());

    const [selectedSiegeWar, setSelectedSiegeWar] = useState('all');
    const [selectedResult, setSelectedResult] = useState('all');
    /** 상대 길드 고르기(사용자 2026-10-31 "공격 왼쪽에 상대 길드도 선택할 수 있게") — 'all' 이면 둘 다 */
    const [selectedOpp, setSelectedOpp] = useState('all');
    const [searchValue, setSearchValue] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const [page, setPage] = useState(1);

    const [selectedPost, setSelectedPost] = useState<any>(null);
    // 뒤로 가기: 열린 기록 창 → 목록 → 매치업 고르기 → 시즌 고르기 순으로 물린다(hooks/useBackHandler)
    // 단계마다 따로 등록한다 — 앞으로 갈 때마다 방문 기록 칸이 하나씩 쌓여야 뒤로 가기도 한 단계씩 돈다
    useBackHandler(battleStep !== 'season', () => { setBattleStep('season'); setSelectedSeason(0); });
    useBackHandler(battleStep === 'list', () => { setBattleStep('group'); setSelectedSiegeWar('all'); });
    useBackHandler(!!selectedPost, () => setSelectedPost(null));
    const [expandedPosts, setExpandedPosts] = useState<Set<string>>(new Set());

    /**
     * **보고 있는 동안 저절로 갱신.** 예전엔 화면이 처음 그려질 때 한 번만 받아서, 새 전투가 들어와도 새로고침해야 보였다(사용자 지적).
     * 1분마다(화면이 보일 때만), 그리고 다른 창·앱에 갔다 돌아와 이 화면이 다시 보이면 `tick` 을 올린다 — 시즌·그룹·기록이 뒤에서 다시 받고,
     * 달라진 것이 있을 때만 화면을 바꾼다(펼친 기록·스크롤은 그대로).
     */
    const [tick, setTick] = useState(0);
    useEffect(() => {
        let last = Date.now();
        const bump = () => {
            if (document.visibilityState !== 'visible') return;
            if (Date.now() - last < 15_000) return;   // 연달아 오는 focus·visibility 를 한 번으로
            last = Date.now();
            setTick((t) => t + 1);
        };
        const timer = window.setInterval(bump, 60_000);
        document.addEventListener('visibilitychange', bump);
        window.addEventListener('focus', bump);
        // 같은 탭에서 다른 페이지에 갔다가 뒤로가기로 돌아오면 브라우저가 멈춰 둔 화면(bfcache)을 그대로 되살린다 — 그때 오는 것
        window.addEventListener('pageshow', bump);
        return () => {
            window.clearInterval(timer);
            document.removeEventListener('visibilitychange', bump);
            window.removeEventListener('focus', bump);
            window.removeEventListener('pageshow', bump);
        };
    }, []);

    // 닉네임 → 프로필 이미지. 게임에서 잘라 올린 얼굴이 여기 실려 온다.
    useEffect(() => {
        if (cache.avatars) {
            setAvatars(cache.avatars.avatars);
            setKicked(new Set(cache.avatars.kicked));
        }
        fetch('/api/siege-battles/avatars')
            .then((r) => r.json())
            .then((d) => {
                if (!d.success) return;
                cache.avatars = { avatars: d.avatars || {}, kicked: d.kicked || [] };
                setAvatars(cache.avatars.avatars);
                setKicked(new Set(cache.avatars.kicked));
            })
            .catch(() => { /* 남겨 둔 값이 있으면 그걸로 버틴다 */ });
    }, []);

    /**
     * 현재 시즌과 기록이 있는 시즌 목록. 서버가 그룹 문서에서 계산해 준다.
     *
     * 좀처럼 바뀌지 않는 값이라 **탭에 저장해두고 먼저 그린다**(세션 저장소).
     * 그래야 화면을 열 때마다 "불러오는 중"이 깜빡이지 않는다. 저장된 값을 쓰더라도
     * 뒤에서 새로 받아 갱신한다 — 새 점령전이 들어오면 목록이 늘어난다.
     */
    useEffect(() => {
        /**
         * **`localStorage` 에 나이 제한 없이** 남긴다.
         *
         * 예전에는 세션 저장소에 10분만 뒀다. 그래서 탭을 새로 열거나 10분 뒤에 들어오면
         * 저장된 값이 무시되고, 시즌 카드가 뜨기까지 "시즌을 불러오는 중…"이 보였다
         * (응답 자체는 20~375ms 지만 폰에서는 왕복이 더 걸린다).
         *
         * 시즌 목록은 새 점령전이 수집될 때만 늘어나는 값이라, 저장된 값을 먼저 그려도
         * 틀릴 일이 없다 — 아래에서 곧 새로 받아 갱신한다. 옛 키(v1)는 쓰지 않는다.
         */
        const KEY = 'siege-seasons-v2';

        try {
            const raw = localStorage.getItem(KEY);
            if (raw) {
                const saved = JSON.parse(raw);
                if (saved?.info?.seasons) setSeasonInfo(saved.info);
            }
        } catch {
            /* 저장소를 못 읽어도 그냥 받아온다 */
        }

        fetch('/api/siege-battles/seasons')
            .then((r) => r.json())
            .then((d) => {
                if (!d.success) return;
                const info = { current: d.current, seasons: d.seasons || [] };
                setSeasonInfo(info);
                try {
                    localStorage.setItem(KEY, JSON.stringify({ at: Date.now(), info }));
                } catch {
                    /* 저장 실패는 넘긴다 */
                }
            })
            .catch(() => { /* 저장된 값이 있으면 그걸로 버틴다 */ });
    }, [tick]);

    /** 기록이 있는 시즌 목록(최신순). 아직 아무 기록도 없으면 현재 시즌 하나. */
    const seasons = useMemo(() => {
        if (!seasonInfo) return [];
        return seasonInfo.seasons.length ? seasonInfo.seasons : [seasonInfo.current];
    }, [seasonInfo]);

    /**
     * 매치업 목록. 그룹마다 시즌이 적혀 있다.
     *
     * **탭에도 남긴다(세션 저장소).** 모듈 변수만 쓰면 새로고침하면 사라져서, 시즌을 고른
     * 뒤 카드가 뜨기까지 1초쯤 빈 화면이 보였다(실측). 저장된 값으로 먼저 그리고 뒤에서
     * 새로 받아 갱신한다 — 카드의 승/패는 점령전이 새로 수집될 때만 바뀐다.
     */
    useEffect(() => {
        const saved = cache.groups.get(logType) || readBattleGroups(logType) || null;
        if (saved && !tick) {
            cache.groups.set(logType, saved);
            setGroups(saved);
        }
        fetch(`/api/siege-battles/groups?logType=${logType}`)
            .then((r) => r.json())
            .then((d) => {
                if (!d.success) return;
                setSyncedAt(d.syncedAt ?? null);
                writeBattleGroups(logType, d.groups || []);
                // 같으면 상태를 건드리지 않는다 — 배열이 새로 오면 아래 기록 받기가 다시 돈다
                if (saved && JSON.stringify(saved) === JSON.stringify(d.groups)) return;
                cache.groups.set(logType, d.groups);
                setGroups(d.groups);
            })
            .catch(() => setIsLoading(false));
    }, [logType, tick]);

    /** 고른 시즌의 그룹들 */
    const seasonGroups = useMemo(
        () => groups.filter((g) => g.season === selectedSeason),
        [groups, selectedSeason]
    );

    /**
     * 기록은 **목록을 볼 때, 고른 매치업만** 받는다.
     *
     * 예전에는 시즌을 고르는 순간 그 시즌 **모든 그룹**의 기록을 받았다. 실측으로
     * 5개 그룹 1.78MB / 1.7초였고(NAS), 사람은 그중 한 그룹만 본다. 그래서
     *
     *   - 그룹 선택 화면에서는 아무것도 받지 않는다. 카드의 승/패는 그룹 목록에
     *     실려 오는 집계값을 쓴다(1KB).
     *   - 매치업을 고르면 그 그룹만 받는다.
     *   - `전체` 를 고르면 그때 다 받는다 — 사람이 일부러 고른 경우다.
     */
    useEffect(() => {
        if (battleStep !== 'list') {
            setIsLoading(false);
            return;
        }
        if (!selectedSeason || !seasonGroups.length) return;

        const targets =
            selectedSiegeWar === 'all'
                ? seasonGroups
                : seasonGroups.filter((g) => g.groupName === selectedSiegeWar);
        if (!targets.length) return;

        const key = `${logType}:${selectedSeason}:${selectedSiegeWar}`;
        const saved = cache.battles.get(key);
        // 남겨 둔 기록이 있으면 먼저 그린다. "불러오는 중"은 처음 볼 때만 뜬다
        if (saved) setBattles(saved);
        setIsLoading(!saved);

        // 이름표(unit-index)와 기록을 나란히 받는다. 이름표는 보통 저장돼 있어 즉시 온다
        Promise.all([
            loadUnitIndex(),
            Promise.all(
                targets.map((g) =>
                    fetch(`/api/siege-battles?groupId=${g.groupId}&limit=500&logType=${logType}&decks=ids`)
                        .then((r) => r.json())
                        .then((d) => (d.success ? (d.items as SlimBattle[]) : []))
                        .catch(() => [] as SlimBattle[])
                )
            )
        ])
            .then(([index, chunks]) => {
                const all = chunks.flat().map((b) => hydrateBattle(b, index));
                // 다시 받은 것이 그대로면 화면을 건드리지 않는다(저절로 갱신할 때 깜빡이지 않게)
                const sig = (list: Battle[]) => `${list.length}:${list.map((b) => `${b.siegeId}-${b.logId}${b.result}`).join(',')}`;
                if (saved && sig(saved) === sig(all)) return;
                cache.battles.set(key, all);
                setBattles(all);
            })
            .finally(() => setIsLoading(false));
    }, [battleStep, selectedSeason, seasonGroups, logType, selectedSiegeWar, tick]);

    /**
     * 길드원 탭은 [공격/방어]·[전체/내 기록/승리/패배]·검색의 영향을 받지 않는다(사용자 2026-10-22).
     * 늘 그 그룹의 **공격 기록 전부**로 센다 — [방어] 를 고른 동안은 공격 기록을 따로 받아 둔다.
     */
    useEffect(() => {
        if (battleStep !== 'list' || logType === 1) { setAttackBattles(null); return; }
        if (!selectedSeason || !seasonGroups.length) return;
        const targets = selectedSiegeWar === 'all' ? seasonGroups : seasonGroups.filter((g) => g.groupName === selectedSiegeWar);
        if (!targets.length) return;
        const key = `1:${selectedSeason}:${selectedSiegeWar}`;
        const saved = cache.battles.get(key);
        if (saved) setAttackBattles(saved);
        let alive = true;
        Promise.all([
            loadUnitIndex(),
            Promise.all(targets.map((g) =>
                fetch(`/api/siege-battles?groupId=${g.groupId}&limit=500&logType=1&decks=ids`)
                    .then((r) => r.json())
                    .then((d) => (d.success ? (d.items as SlimBattle[]) : []))
                    .catch(() => [] as SlimBattle[])
            ))
        ]).then(([index, chunks]) => {
            const all = chunks.flat().map((b) => hydrateBattle(b, index));
            cache.battles.set(key, all);
            if (alive) setAttackBattles(all);
        });
        return () => { alive = false; };
    }, [battleStep, selectedSeason, seasonGroups, logType, selectedSiegeWar, tick]);

    /** siegeId -> 그룹. 기록은 자기 그룹만 가리키므로 이름·시즌은 여기서 얻는다. */
    const groupBySiege = useMemo(() => {
        const out = new Map<number, SiegeGroup>();
        groups.forEach((g) => out.set(g.siegeId, g));
        return out;
    }, [groups]);

    const allPosts = useMemo(
        () =>
            battles.map((b) => {
                const g = groupBySiege.get(b.siegeId);
                return toPost(
                    b,
                    g?.groupName || '기타',
                    logType,
                    g?.season ?? seasonInfo?.current ?? 0,
                    avatars,
                    kicked
                );
            }),
        [battles, groupBySiege, logType, seasonInfo, avatars, kicked]
    );

    const filteredPosts = useMemo(() => {
        let list = allPosts;
        if (selectedSiegeWar !== 'all') list = list.filter((p) => p.group === selectedSiegeWar);
        if (selectedOpp !== 'all') list = list.filter((p) => p.siegeWarImg === selectedOpp);
        if (selectedResult === 'my') {
            if (user?.nickname) list = list.filter((p) => p.nickname === user.nickname);
        } else if (selectedResult !== 'all') {
            list = list.filter((p) => p.result === selectedResult);
        }
        const kw = appliedSearch.trim();
        if (kw) {
            list = list.filter((p) =>
                p.nickname.includes(kw) ||
                p.defenderNickname.includes(kw) ||
                [...p.attackTeam, ...p.defenseTeam].some((u) => (u.displayName || '').includes(kw) || (u.name || '').toLowerCase().includes(kw.toLowerCase()))
            );
        }
        return list;
    }, [allPosts, selectedSiegeWar, selectedOpp, selectedResult, appliedSearch, user?.nickname]);

    /** 고른 매치업에 나온 상대 길드들(기록의 상대 길드 이름) — 가나다순 */
    const oppGuilds = useMemo(() => {
        const set = new Set<string>();
        for (const p of allPosts) if ((selectedSiegeWar === 'all' || p.group === selectedSiegeWar) && p.siegeWarImg) set.add(p.siegeWarImg);
        return [...set].sort((a, b) => a.localeCompare(b, 'ko'));
    }, [allPosts, selectedSiegeWar]);

    // 매치업·시즌을 바꾸면 상대 길드 고르기는 처음(전체)으로 — 다른 매치업엔 그 길드가 없다
    useEffect(() => { setSelectedOpp('all'); }, [selectedSiegeWar, selectedSeason]);

    /** 길드원 탭이 세는 기록 — 공격 기록, 그룹만 거른다(위 주석) */
    const memberPosts = useMemo(() => {
        const src = logType === 1 ? battles : (attackBattles || []);
        return src
            .map((b) => {
                const g = groupBySiege.get(b.siegeId);
                return toPost(b, g?.groupName || '기타', 1, g?.season ?? seasonInfo?.current ?? 0, avatars, kicked);
            })
            .filter((p) => selectedSiegeWar === 'all' || p.group === selectedSiegeWar);
    }, [logType, battles, attackBattles, groupBySiege, seasonInfo, avatars, kicked, selectedSiegeWar]);

    const displayedPosts = useMemo(() => filteredPosts.slice(0, page * POSTS_PER_PAGE), [filteredPosts, page]);

    useEffect(() => { setPage(1); }, [selectedSiegeWar, selectedOpp, selectedResult, appliedSearch, selectedSeason]);

    const togglePost = useCallback((postId: string) => {
        setExpandedPosts((prev) => {
            const next = new Set(prev);
            if (next.has(postId)) next.delete(postId); else next.add(postId);
            return next;
        });
    }, []);

    const shared = {
        posts: displayedPosts,
        allPosts,
        filteredPosts,
        memberPosts,
        // 고른 시즌의 그룹만 넘긴다. 예전에는 전 시즌 그룹이 매치업 목록에 섞였다.
        prefetchedGroups: seasonGroups,
        /**
         * 그룹 카드에 찍을 승/패. 서버가 그룹 목록에 실어 준 집계값이다.
         *
         * 예전에는 이걸 그리려고 화면이 모든 그룹의 기록을 받아 세었다. 이제 받지 않는다.
         */
        groupWinLoss: Object.fromEntries(
            seasonGroups.map((g) => [
                g.groupName,
                {
                    wins: g.wins ?? 0,
                    losses: g.losses ?? 0,
                    winRate: g.winRate,
                    defenseRate: g.defenseRate
                }
            ])
        ),
        monsterMapping,
        user,
        searchValue,
        setSearchValue,
        onSearch: () => setAppliedSearch(searchValue),
        onSearchReset: () => { setSearchValue(''); setAppliedSearch(''); },
        onPostClick: setSelectedPost,
        selectedPost,
        setSelectedPost,
        expandedPosts,
        togglePost,
        // 이 데이터는 게임에서 자동으로 들어오므로 업로드·삭제가 없다
        onUploadClick: () => {},
        selectedSeason,
        setSelectedSeason,
        selectedSiegeWar,
        setSelectedSiegeWar,
        selectedResult,
        setSelectedResult,
        selectedOpp,
        setSelectedOpp,
        oppGuilds,
        onLoadMore: () => setPage((p) => p + 1),
        hasMore: displayedPosts.length < filteredPosts.length,
        isLoading,
        battleStep,
        onSeasonSelect: (season: number) => { setSelectedSeason(season); setSelectedSiegeWar('all'); setBattleStep('group'); },
        onGroupSelect: (group: string) => { setSelectedSiegeWar(group); setBattleStep('list'); },
        onBackToSeason: () => { setBattleStep('season'); setSelectedSeason(0); },
        onBackToGroup: () => { setBattleStep('group'); setSelectedSiegeWar('all'); },
        totalCount: filteredPosts.length,
        // 방어 기록도 guildName 이 우리 길드다(박종민(설탕) vs 까망☆(완댜곤듀)).
        // 공덱/방덱 어느 쪽이든 매치업 배너는 같아야 한다.
        myGuildName: battles[0]?.guildName || '설탕',
        seasonList: seasons,
        logType,
        syncedAt,
        // PostList 는 number 로 받으므로 1/2 로 좁혀서 넘긴다
        setLogType: (v: number) => setLogType(v === 2 ? 2 : 1)
    };

    return isMobile ? <PostListMobile {...shared} /> : <PostListDesktop {...shared} />;
};

export default SiegeBattleList;
